/**
 * VerifyEmailPage — the branded landing citizens hit after tapping the
 * verification link embedded in their welcome email.
 *
 * Prior UX: `GET /api/auth/verify-email?token=X` returned raw JSON,
 * which the citizen's browser rendered as a JSON blob. Cohort testers
 * saw an unpolished technical response instead of a welcome page.
 *
 * Fix (LCM O-17): the email link points to the frontend at
 * `${_APP_BASE_URL}/verify-email?token=X`. This page runs the API call
 * client-side, then displays a branded confirmation with a clear "Open
 * the VeraLok app" CTA. Idempotent — a citizen who taps the link twice
 * sees the same success state.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, AlertCircle, Clock, Loader2, ArrowRight } from "lucide-react";
import VeraLokLogo from "./VeraLokLogo";
import "./design-system.css";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const STATES = {
  LOADING: "loading",
  VERIFIED: "verified",
  ALREADY_VERIFIED: "already_verified",
  EXPIRED: "expired",
  NOT_FOUND: "not_found",
  MISSING_TOKEN: "missing_token",
  ERROR: "error",
};

function useTokenFromQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search).get("token");
}

export default function VerifyEmailPage() {
  const token = useTokenFromQuery();
  const [state, setState] = useState(STATES.LOADING);
  const [email, setEmail] = useState("");
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return; // guard StrictMode double-invoke
    firedRef.current = true;

    if (!token) {
      setState(STATES.MISSING_TOKEN);
      return;
    }

    const url = `${API_BASE}/api/auth/verify-email?token=${encodeURIComponent(token)}`;
    fetch(url, { method: "GET" })
      .then(async (r) => {
        let body = null;
        try { body = await r.json(); } catch (_) { /* body may be empty */ }
        if (r.ok) {
          setEmail(body?.email || "");
          setState(STATES.VERIFIED);
          return;
        }
        const detail = body?.detail;
        if (r.status === 404 && detail === "verification_token_not_found") {
          setState(STATES.NOT_FOUND);
        } else if (r.status === 410 && detail === "verification_token_expired") {
          setState(STATES.EXPIRED);
        } else {
          setState(STATES.ERROR);
        }
      })
      .catch(() => setState(STATES.ERROR));
  }, [token]);

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6 py-16"
      style={{
        background:
          "radial-gradient(1200px 600px at 50% -10%, rgba(30,144,255,0.14), transparent 60%), var(--vlk-ink-100)",
        color: "var(--vlk-fog-100)",
        fontFamily: "var(--vlk-font-body, 'Inter', system-ui, sans-serif)",
      }}
      data-testid="verify-email-page"
    >
      <div className="w-full max-w-lg">
        <VerifyCard state={state} email={email} />
      </div>
    </div>
  );
}

function VerifyCard({ state, email }) {
  const config = CARD_CONFIG[state] || CARD_CONFIG[STATES.ERROR];

  return (
    <div
      className="rounded-2xl border p-8 sm:p-10"
      style={{
        background: "linear-gradient(180deg, rgba(17,22,35,0.85) 0%, rgba(11,15,23,0.85) 100%)",
        borderColor: "rgba(56,176,255,0.18)",
        boxShadow:
          "0 10px 40px -12px rgba(0,0,0,0.75), 0 0 0 1px rgba(56,176,255,0.06) inset",
        backdropFilter: "blur(12px)",
      }}
      data-testid={`verify-email-card-${state}`}
    >
      <div className="flex flex-col items-center text-center">
        <div className="mb-6">
          <VeraLokLogo size={72} showWordmark={false} />
        </div>

        <StatusIcon tone={config.tone} icon={config.icon} spinning={state === STATES.LOADING} />

        <h1
          className="mt-6 text-2xl sm:text-3xl font-semibold tracking-tight"
          style={{ fontFamily: "var(--vlk-font-display, 'Cabinet Grotesk', 'Inter', sans-serif)" }}
          data-testid="verify-email-title"
        >
          {config.title}
        </h1>

        <p
          className="mt-3 text-sm sm:text-base leading-relaxed max-w-md"
          style={{ color: "var(--vlk-fog-300)" }}
          data-testid="verify-email-message"
        >
          {typeof config.body === "function" ? config.body({ email }) : config.body}
        </p>

        {config.cta && (
          <div className="mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            {config.cta.map((c) => (
              <CtaButton key={c.label} to={c.to} tone={c.tone} testid={c.testid}>
                {c.label}
              </CtaButton>
            ))}
          </div>
        )}

        <p
          className="mt-10 text-xs uppercase tracking-[0.18em]"
          style={{ color: "var(--vlk-fog-500)" }}
        >
          VeraLok — Identity you control
        </p>
      </div>
    </div>
  );
}

function StatusIcon({ tone, icon: Icon, spinning }) {
  const bg = {
    brand: "rgba(56,176,255,0.14)",
    success: "rgba(34,197,94,0.14)",
    warn: "rgba(245,158,11,0.14)",
    danger: "rgba(239,68,68,0.14)",
  }[tone];
  const color = {
    brand: "#38B0FF",
    success: "var(--vlk-success)",
    warn: "var(--vlk-warn)",
    danger: "var(--vlk-danger)",
  }[tone];
  return (
    <div
      className="h-14 w-14 rounded-full flex items-center justify-center"
      style={{ background: bg, color }}
      data-testid="verify-email-status-icon"
    >
      <Icon className={`h-7 w-7 ${spinning ? "animate-spin" : ""}`} strokeWidth={2} />
    </div>
  );
}

function CtaButton({ to, tone = "primary", children, testid }) {
  const isPrimary = tone === "primary";
  const style = isPrimary
    ? {
        background: "linear-gradient(135deg, #38B0FF, #0F6FD1)",
        color: "#FFFFFF",
        boxShadow: "0 6px 20px -6px rgba(56,176,255,0.55)",
      }
    : {
        background: "transparent",
        color: "var(--vlk-fog-200)",
        border: "1px solid rgba(184,193,214,0.24)",
      };
  return (
    <Link
      to={to}
      data-testid={testid}
      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-transform duration-200 hover:-translate-y-0.5"
      style={style}
    >
      {children}
      {isPrimary && <ArrowRight className="h-4 w-4" />}
    </Link>
  );
}

const CARD_CONFIG = {
  [STATES.LOADING]: {
    tone: "brand",
    icon: Loader2,
    title: "Verifying your email…",
    body: "Hang tight while we confirm your account.",
  },
  [STATES.VERIFIED]: {
    tone: "success",
    icon: CheckCircle2,
    title: "Email verified",
    body: ({ email }) =>
      email
        ? `${email} is now confirmed. Open the VeraLok app to complete enrollment.`
        : "Your email is now confirmed. Open the VeraLok app to complete enrollment.",
    cta: [
      { label: "Open the VeraLok app", to: "/citizen", tone: "primary", testid: "verify-email-cta-app" },
      { label: "Back to home", to: "/", tone: "ghost", testid: "verify-email-cta-home" },
    ],
  },
  [STATES.EXPIRED]: {
    tone: "warn",
    icon: Clock,
    title: "This link has expired",
    body: "Verification links are valid for 24 hours. Please sign in to the VeraLok app and request a fresh verification email.",
    cta: [
      { label: "Open the VeraLok app", to: "/citizen", tone: "primary", testid: "verify-email-cta-app" },
      { label: "Back to home", to: "/", tone: "ghost", testid: "verify-email-cta-home" },
    ],
  },
  [STATES.NOT_FOUND]: {
    tone: "danger",
    icon: AlertCircle,
    title: "This verification link is no longer valid",
    body: "The link may have already been used or has been rotated. If your account is already verified you can safely continue to the app.",
    cta: [
      { label: "Open the VeraLok app", to: "/citizen", tone: "primary", testid: "verify-email-cta-app" },
      { label: "Back to home", to: "/", tone: "ghost", testid: "verify-email-cta-home" },
    ],
  },
  [STATES.MISSING_TOKEN]: {
    tone: "danger",
    icon: AlertCircle,
    title: "Missing verification token",
    body: "It looks like the verification link was incomplete. Open the original email on this device and tap the button again.",
    cta: [
      { label: "Back to home", to: "/", tone: "primary", testid: "verify-email-cta-home" },
    ],
  },
  [STATES.ERROR]: {
    tone: "danger",
    icon: AlertCircle,
    title: "Something went wrong",
    body: "We couldn't verify your email right now. Please try tapping the link again in a moment or contact support if this keeps happening.",
    cta: [
      { label: "Back to home", to: "/", tone: "primary", testid: "verify-email-cta-home" },
    ],
  },
};
